// src/pages/SignIn.js
import React, { useState } from 'react';
import { auth, googleProvider, db } from '../lib/firebase';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { getMerchant } from '../lib/MerchantModel';
import { useNavigate } from 'react-router-dom';

export default function SignIn() {
  const navigate  = useNavigate();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const afterAuth = async (user) => {
    // Check admin first — admins have no merchant doc
    const adminSnap = await getDoc(doc(db, 'admins', user.email));
    if (adminSnap.exists()) {
      navigate('/admin');
      return;
    }

    // Regular merchant login
    const merchant = await getMerchant(user.uid);
    if (!merchant) {
      await auth.signOut();
      setError('No partner account found. Please contact Checks Intel to get set up.');
      return;
    }
    navigate('/dashboard');
  };

  const handleGoogle = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await afterAuth(result.user);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      await afterAuth(cred.user);
    } catch (e) {
      const code = e.code;
      if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
        setError('Incorrect email or password.');
      } else if (code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again in a few minutes.');
      } else {
        setError(e.message.replace('Firebase: ', '').replace(/\(auth.*\)/, '').trim());
      }
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#F7F4EF',
      fontFamily: "'Outfit', sans-serif",
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=Outfit:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; }
        input { font-family: 'Outfit', sans-serif; }
        .signin-input { width: 100%; padding: 12px 16px; border: 1px solid rgba(0,0,0,0.1); border-radius: 12px; background: #fff; font-size: 14px; color: #1a1a1a; outline: none; transition: border-color 0.2s; }
        .signin-input:focus { border-color: #4000E4; }
        .btn-primary { width: 100%; padding: 13px; background: #4000E4; color: #fff; border: none; border-radius: 12px; font-family: 'Outfit', sans-serif; font-size: 14px; font-weight: 600; letter-spacing: 0.05em; cursor: pointer; transition: opacity 0.2s, transform 0.2s; }
        .btn-primary:hover { opacity: 0.9; transform: translateY(-1px); }
        .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
        .btn-google { width: 100%; padding: 13px; background: #fff; color: #1a1a1a; border: 1px solid rgba(0,0,0,0.12); border-radius: 12px; font-family: 'Outfit', sans-serif; font-size: 14px; font-weight: 500; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 10px; transition: box-shadow 0.2s, transform 0.2s; }
        .btn-google:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.1); transform: translateY(-1px); }
        .divider { display: flex; align-items: center; gap: 12px; color: #ccc; font-size: 12px; margin: 16px 0; }
        .divider::before, .divider::after { content: ''; flex: 1; height: 1px; background: #e5e5e5; }
        .error-box { background: #fef2f2; border: 1px solid #fecaca; border-radius: 10px; padding: 10px 14px; font-size: 13px; color: #dc2626; }
      `}</style>

      <div style={{ width: '100%', maxWidth: '420px' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '28px', fontWeight: 600, letterSpacing: '0.15em', color: '#1a1a1a', textTransform: 'uppercase', marginBottom: '6px' }}>
            Checks Intel
          </div>
          <div style={{ fontSize: '13px', color: '#999', letterSpacing: '0.05em' }}>Merchant Partner Platform</div>
        </div>

        <div style={{ background: '#fff', borderRadius: '20px', padding: '36px', boxShadow: '0 4px 32px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.06)' }}>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', fontWeight: 500, color: '#1a1a1a', marginBottom: '4px' }}>Welcome back</h2>
          <p style={{ fontSize: '13px', color: '#aaa', marginBottom: '28px' }}>Sign in to your partner dashboard.</p>

          <button className="btn-google" onClick={handleGoogle} disabled={loading}>
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </button>

          <div className="divider">or</div>

          <form onSubmit={handleSignIn} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div>
              <label style={labelStyle}>Email</label>
              <input className="signin-input" type="email" placeholder="you@merchant.com" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <input className="signin-input" type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" />
            </div>
            {error && <div className="error-box">{error}</div>}
            <button className="btn-primary" type="submit" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p style={{ textAlign: 'center', fontSize: '12px', color: '#bbb', marginTop: '20px', lineHeight: 1.5 }}>
            Don't have access yet?{' '}
            <a href="mailto:contact@checksintel.com" style={{ color: '#4000E4', textDecoration: 'none', fontWeight: 600 }}>Contact us</a>
          </p>
        </div>

        <p style={{ textAlign: 'center', fontSize: '12px', color: '#bbb', marginTop: '24px' }}>
          © 2025 Checks Intel Inc. · <a href="https://checksintel.com" style={{ color: '#bbb' }}>checksintel.com</a>
        </p>
      </div>
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#888', marginBottom: '6px' };